import { domToCanvas } from 'modern-screenshot';
import type { ViewSnapshot } from '@api/apps';
import type { SnapshotSource, SourceFrame } from './viewSnapshotContext';

/** Matches the server's resize, so nothing is sent only to be shrunk. */
export const MAX_LONG_EDGE_PX = 1568;
export const JPEG_QUALITY = 0.85;
export const CAPTURE_TIMEOUT_MS = 2000;

export class CaptureFailure extends Error {
  constructor(readonly reason: 'timeout' | 'empty' | 'encode') {
    super(`View snapshot failed: ${reason}`);
  }
}

const frameBitmap = async (frame: SourceFrame): Promise<ImageBitmap | null> => {
  if (!frame) return null;
  const blob = typeof frame === 'string' ? await (await fetch(frame)).blob() : frame;
  return createImageBitmap(blob);
};

// Iframes are opaque to the page renderer and come back blank anyway; their
// own capture is drawn in their place.
const notFrame = (node: Node) => !(node instanceof HTMLIFrameElement);

// JPEG has no transparency, so whatever the root leaves unpainted would turn black.
const pageBackground = (): string => {
  const color = getComputedStyle(document.body).backgroundColor;
  return !color || color === 'transparent' || color === 'rgba(0, 0, 0, 0)' ? '#ffffff' : color;
};

const toJpeg = (canvas: HTMLCanvasElement): Promise<Blob> =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new CaptureFailure('encode'))),
      'image/jpeg',
      JPEG_QUALITY,
    );
  });

const base64Of = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).replace(/^data:[^,]*,/, ''));
    reader.onerror = () => reject(new CaptureFailure('encode'));
    reader.readAsDataURL(blob);
  });

/** Draws one source over its spot; one that cannot be captured leaves the page beneath. */
const drawSource = async (
  context: CanvasRenderingContext2D,
  origin: DOMRect,
  scale: number,
  source: SnapshotSource,
) => {
  const box = source.elementRef.current?.getBoundingClientRect();
  if (!box || box.width === 0 || box.height === 0) return;
  const bitmap = await Promise.resolve()
    .then(source.capture)
    .then(frameBitmap)
    .catch(() => null);
  if (!bitmap) return;
  context.drawImage(
    bitmap,
    (box.left - origin.left) * scale,
    (box.top - origin.top) * scale,
    box.width * scale,
    box.height * scale,
  );
  bitmap.close();
};

const render = async (root: HTMLElement, sources: readonly SnapshotSource[]): Promise<ViewSnapshot> => {
  const origin = root.getBoundingClientRect();
  const longEdge = Math.max(origin.width, origin.height);
  if (longEdge === 0) throw new CaptureFailure('empty');
  const scale = Math.min(1, MAX_LONG_EDGE_PX / longEdge);
  const canvas = await domToCanvas(root, { scale, filter: notFrame, backgroundColor: pageBackground() });
  const context = canvas.getContext('2d');
  if (!context) throw new CaptureFailure('encode');
  // The canvas may come back at device pixels; draw sources in its own units.
  const drawScale = canvas.width / origin.width;
  for (const source of sources) {
    await drawSource(context, origin, drawScale, source);
  }
  return { mediaType: 'image/jpeg', base64: await base64Of(await toJpeg(canvas)) };
};

export const snapshotOfImage = async (image: Blob): Promise<ViewSnapshot> => {
  const bitmap = await createImageBitmap(image);
  try {
    const longEdge = Math.max(bitmap.width, bitmap.height);
    if (longEdge === 0) throw new CaptureFailure('empty');
    const scale = Math.min(1, MAX_LONG_EDGE_PX / longEdge);
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    const context = canvas.getContext('2d');
    if (!context) throw new CaptureFailure('encode');
    context.fillStyle = pageBackground();
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    return { mediaType: 'image/jpeg', base64: await base64Of(await toJpeg(canvas)) };
  } finally {
    bitmap.close();
  }
};

/** What the user sees inside {@link root}, with each source drawn from its own capture. */
export const captureView = (
  root: HTMLElement,
  sources: readonly SnapshotSource[],
  timeoutMs = CAPTURE_TIMEOUT_MS,
): Promise<ViewSnapshot> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new CaptureFailure('timeout')), timeoutMs);
  });
  return Promise.race([render(root, sources), timeout]).finally(() => clearTimeout(timer));
};
